import { useAuth } from "react-oidc-context";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import type { FC } from "react";
import { EChart as RawEChart } from "@kbox-labs/react-echarts";

import config from "./config.json";
import { DEFAULT_PIPELINE_SORT, naturalDirection, nextPipelineSort, sortPipelineLots } from "./adminPipeline";
import type { PipelineLot, PipelineSort, PipelineSortKey } from "./adminPipeline";
import BudgetsSection from "./BudgetsSection";
import { METRIC_COLS, fmtNum, fmtUsdPerCall, headerGroups, metricValue } from "./apiCosts";
import type { MetricKey, UsageMetrics } from "./apiCosts";

const EChart = RawEChart as unknown as FC<any>;

type Metrics = UsageMetrics;
interface FeatureRow {
  feature: string;
  perModel: Record<string, Metrics>;
  total: Metrics;
}
interface ModelRow { model: string; cost: number; }
interface DailyPoint {
  date: string;
  cost: number;
  calls: number;
  prompt: number;
  output: number;
  thoughts: number;
  byFeature?: Record<string, number>; // feature -> cost_micros
  tendersProcessed?: number | null;   // lots the tender pipeline summarised that day (null = unknown)
}
interface UsageResponse {
  days: number;
  windows: { today: number; yesterday: number; last_7d: number; last_30d: number };
  totals: { calls: number; prompt: number; output: number; thoughts: number; cost: number };
  models: string[];
  byFeature: FeatureRow[];
  byModel: ModelRow[];
  daily: DailyPoint[];
  currency: string;
  costUnit: "micros";
}
// One day in detail (GET /admin/llm-usage?day=): the rollup's own rows, one
// per company x model x feature, plus what the tender pipeline did that day.
interface DayUsageRow extends Metrics {
  company: string;
  model: string;
  feature: string;
  cached: number;
}
interface DayPipeline {
  tenders: PipelineLot[];
  tender_count: number | null;
  documents: number | null;
  scores: number | null;
  reused: number | null;      // documents completed from an identical earlier copy, no LLM call
  error?: string;
}
interface DayUsageResponse {
  day: string;
  rows: DayUsageRow[];
  totals: Metrics & { cached: number };
  models: string[];
  pipeline: DayPipeline;
}

const PROJECTS = [
  { value: "tenderq",       label: "TenderQ" },
  { value: "qabot",         label: "QABot" },
  { value: "qinterrogator", label: "QInterrogator" },
];
const MODELS = [
  { value: "all", label: "All models" },
  { value: "gemini-3.1-flash-lite", label: "gemini-3.1-flash-lite" },
  { value: "gemini-3.7-flash",      label: "gemini-3.7-flash" },
  { value: "gemini-3.1-pro-preview",        label: "gemini-3.1-pro-preview" },
];

// Canonical display order for known models; unknown ids fall back to alphabetical.
const KNOWN_MODEL_ORDER = [
  "gemini-3.1-flash-lite",
  "gemini-3.7-flash",
  "gemini-3.1-pro-preview",
];

interface FeatureGroup {
  id: string;
  label: string;
  color: string;
  match: (f: string) => boolean;
}

// Per-project feature taxonomy. The /admin/llm-usage endpoint returns ALL
// features written to the shared usage table (qabot_api_caller writes them
// regardless of which project invoked it), so we partition client-side.
const FEATURE_GROUPS_BY_PROJECT: Record<string, FeatureGroup[]> = {
  tenderq: [
    { id: "proposal",   label: "Proposal agent",   color: "#1976d2", match: (f) => f.startsWith("proposal_") },
    { id: "review",     label: "Review agent",     color: "#9c27b0", match: (f) => f.startsWith("review_") },
    { id: "onboarding", label: "Onboarding agent", color: "#2e7d32", match: (f) => f.startsWith("onboarding_") },
    { id: "chat",       label: "Chat",             color: "#ed6c02", match: (f) => f.startsWith("chat") },
    { id: "etl",        label: "ETL",              color: "#00838f", match: (f) => f.startsWith("etl_") },
    { id: "other",      label: "Other",            color: "#757575", match: () => true },
  ],
  qabot: [
    { id: "main_chat",      label: "Main chat",          color: "#1976d2", match: (f) => f === "qabot_main_chat" || f.startsWith("qabot_main_chat") },
    { id: "glueup_extract", label: "Glue Up extraction", color: "#2e7d32", match: (f) => f === "glueup_extract" },
    { id: "glueup_other",   label: "Glue Up (other)",    color: "#ed6c02", match: (f) => f.startsWith("glueup_") },
    { id: "qabot_other",    label: "QABot (other)",      color: "#9c27b0", match: (f) => f.startsWith("qabot_") },
    { id: "other",          label: "Other",              color: "#757575", match: () => true },
  ],
  qinterrogator: [
    { id: "main_chat", label: "Main Chat", color: "#1976d2", match: (f) => f.startsWith("qinterrogator_") },
    { id: "other",     label: "Other",     color: "#757575", match: () => true },
  ],
};

// Decide which project owns a given feature, by name. Used to partition the
// raw API response (which mixes all projects) into per-project slices.
const featureToProject = (feature: string): string => {
  if (feature.startsWith("qabot_") || feature.startsWith("glueup_")) return "qabot";
  if (feature.startsWith("qinterrogator_")) return "qinterrogator";
  return "tenderq";
};

const groupsForProject = (project: string): FeatureGroup[] =>
  FEATURE_GROUPS_BY_PROJECT[project] ?? FEATURE_GROUPS_BY_PROJECT.tenderq;

const groupForFeature = (f: string, project: string): FeatureGroup => {
  const groups = groupsForProject(project);
  return groups.find((g) => g.match(f)) ?? groups[groups.length - 1];
};

const ZERO_METRICS: Metrics = { calls: 0, prompt: 0, output: 0, thoughts: 0, cost: 0 };
const addMetrics = (a: Metrics, b: Metrics): Metrics => ({
  calls:    a.calls    + b.calls,
  prompt:   a.prompt   + b.prompt,
  output:   a.output   + b.output,
  thoughts: a.thoughts + b.thoughts,
  cost:     a.cost     + b.cost,
});

const orderModels = (models: string[]) => {
  const known = KNOWN_MODEL_ORDER.filter((m) => models.includes(m));
  const unknown = models.filter((m) => !KNOWN_MODEL_ORDER.includes(m)).sort();
  return [...known, ...unknown];
};

const microsToUsd = (m: number) => m / 1_000_000;
const fmtUsd = (m: number) => {
  const usd = microsToUsd(m);
  const whole = Math.trunc(usd);
  const cents = Math.abs(usd - whole).toFixed(2).slice(2);
  return `$${fmtNum(whole)}.${cents}`;
};
// Tenth-of-a-cent precision for table cells, where sub-cent values would
// otherwise all collapse to "$0.00".
const fmtUsdPrecise = (m: number) => {
  const usd = microsToUsd(m);
  const whole = Math.trunc(usd);
  const frac = Math.abs(usd - whole).toFixed(3).slice(2);
  return `$${fmtNum(whole)}.${frac}`;
};
const fmtMetric = (key: MetricKey, value: number) =>
  key === "cost" ? fmtUsdPrecise(value) : key === "per_call" ? fmtUsdPerCall(value) : fmtNum(value);

// One metric cell of the API cost tables.
function MetricCell({ col, m, first, weight }: {
  col: (typeof METRIC_COLS)[number]; m: Metrics; first: boolean; weight?: number;
}) {
  return (
    <TableCell align="right" sx={{
      ...(first ? { borderLeft: "1px solid", borderColor: "divider" } : {}),
      fontWeight: weight ?? (col.key === "cost" ? 600 : undefined),
    }}>
      {fmtMetric(col.key, metricValue(m, col.key))}
    </TableCell>
  );
}

const metricHead = { fontWeight: 500, color: "text.secondary", lineHeight: 1.2 } as const;

// Upper metric header row: In / Out / Cost over their columns; Calls spans both rows.
const metricGroupCells = (keyPrefix: string, leftBorder: boolean) =>
  headerGroups().map((h, i) => (
    <TableCell
      key={`${keyPrefix}.${h.label}`}
      align={h.group ? "center" : "right"}
      colSpan={h.colSpan}
      rowSpan={h.group ? 1 : 2}
      sx={{
        ...metricHead,
        ...(leftBorder && i === 0 ? { borderLeft: "1px solid", borderColor: "divider" } : {}),
        ...(h.group ? { pb: 0.25 } : { verticalAlign: "bottom" }),
      }}
    >
      {h.label}
    </TableCell>
  ));

// Lower metric header row: the columns inside the groups.
const metricLeafCells = (keyPrefix: string) =>
  METRIC_COLS.filter((c) => c.group).map((c) => (
    <TableCell key={`${keyPrefix}.${c.key}`} align="right" sx={{ ...metricHead, fontSize: 11, pt: 0.25 }}>
      {c.label}
    </TableCell>
  ));

function KpiInline({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
    </Stack>
  );
}

function DailyCostChart({
  daily,
  project,
  selectedAgent,
  onDayClick,
}: {
  daily: DailyPoint[];
  project: string;
  selectedAgent: string | null;
  onDayClick: (date: string) => void;
}) {
  const groups = groupsForProject(project);
  const dates = daily.map((d) => d.date);

  // Per-day total cost limited to features that belong to the selected project,
  // so the tooltip's "Total" matches the visible stacked bars (otherwise
  // selecting "QABot" while a tenderq day exists would show the tenderq total).
  const perDayProjectTotal: Record<string, number> = Object.fromEntries(
    daily.map((d) => {
      let sum = 0;
      for (const [feat, micros] of Object.entries(d.byFeature ?? {})) {
        if (featureToProject(feat) === project) sum += micros;
      }
      return [d.date, sum];
    }),
  );

  // Lots the tender pipeline summarised per day (tooltip line, TenderQ only).
  const perDayProcessed: Record<string, number | null | undefined> = Object.fromEntries(
    daily.map((d) => [d.date, d.tendersProcessed]),
  );

  // Per-day per-agent cost (USD). 0 stacks become invisible automatically.
  const perDayByAgent: Record<string, number>[] = daily.map((d) => {
    const out: Record<string, number> = {};
    for (const g of groups) out[g.id] = 0;
    for (const [feat, micros] of Object.entries(d.byFeature ?? {})) {
      if (featureToProject(feat) !== project) continue;
      out[groupForFeature(feat, project).id] += micros;
    }
    return out;
  });

  const series = groups.map((g) => ({
    id: g.id,
    name: g.label,
    type: "bar",
    stack: "total",
    data: perDayByAgent.map((p) => +microsToUsd(p[g.id] ?? 0).toFixed(4)),
    itemStyle: {
      color: g.color,
      opacity: selectedAgent && selectedAgent !== g.id ? 0.25 : 1,
    },
    emphasis: { focus: "series" },
    cursor: "pointer",
  }));

  return (
    <EChart
      style={{ width: "100%", height: 200 }}
      grid={{ left: 56, right: 16, top: 28, bottom: 36 }}
      legend={{
        type: "scroll",
        top: 0,
        textStyle: { fontSize: 11 },
        selectedMode: false,
      }}
      tooltip={{
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          const arr = Array.isArray(params) ? params : [params];
          if (arr.length === 0) return "";
          const date = arr[0].axisValue;
          const projectTotal = perDayProjectTotal[date] ?? 0;
          const lines = [`<b>${date}</b>`];
          lines.push(`Total: <b>${fmtUsd(projectTotal)}</b>`);
          const processed = perDayProcessed[date];
          if (project === "tenderq" && processed != null) {
            lines.push(`Tenders processed: <b>${fmtNum(processed)}</b>`);
          }
          const sorted = arr
            .filter((p: any) => Number(p.value) > 0)
            .sort((a: any, b: any) => Number(b.value) - Number(a.value));
          for (const p of sorted) {
            lines.push(
              `${p.marker} ${p.seriesName}: ${fmtUsd(Math.round(Number(p.value) * 1_000_000))}`,
            );
          }
          return lines.join("<br/>");
        },
      }}
      xAxis={{
        type: "category",
        data: dates,
        axisLabel: { fontSize: 11, hideOverlap: true },
      }}
      yAxis={{
        type: "value",
        axisLabel: {
          formatter: (v: number) => {
            const whole = Math.trunc(v);
            const cents = Math.abs(v - whole).toFixed(2).slice(2);
            return `$${fmtNum(whole)}.${cents}`;
          },
          fontSize: 11,
        },
      }}
      series={series}
      onClick={(p: any) => {
        // A bar = one day: open that day's breakdown. (The agent filter for
        // the table below is a chip row, not the chart.)
        if (!p || p.componentType !== "series" || !p.name) return;
        onDayClick(String(p.name));
      }}
    />
  );
}

function DayDetailsDialog({
  day,
  project,
  data,
  loading,
  err,
  onClose,
}: {
  day: string | null;
  project: string;
  data: DayUsageResponse | null;
  loading: boolean;
  err: string | null;
  onClose: () => void;
}) {
  const rows = (data?.rows ?? []).filter((r) => featureToProject(r.feature) === project);
  const total = rows.reduce((acc, r) => addMetrics(acc, r), ZERO_METRICS);
  const projectLabel = PROJECTS.find((p) => p.value === project)?.label ?? project;
  const title = day
    ? new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short", year: "numeric" })
    : "";
  const pipeline = data?.pipeline;
  const lotText = (t: PipelineLot) =>
    (t.total_lots ?? 1) > 1 ? `${t.lot_number ?? "?"}${t.lot_title ? ` · ${t.lot_title}` : ""}` : "";
  // Lots sorted by the clicked column (biggest document sets first by
  // default); a new day starts from the default again.
  const [sort, setSort] = useState<PipelineSort>(DEFAULT_PIPELINE_SORT);
  useEffect(() => { setSort(DEFAULT_PIPELINE_SORT); }, [day]);
  const lots = useMemo(() => sortPipelineLots(pipeline?.tenders ?? [], sort), [pipeline, sort]);
  const sortHeader = (key: PipelineSortKey, label: string, align?: "right") => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : naturalDirection(key)}
        onClick={() => setSort(nextPipelineSort(sort, key))}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
  return (
    <Dialog
      open={!!day}
      onClose={onClose}
      maxWidth={false}
      // Ten columns need room: nearly the whole viewport, not MUI's 960 px default.
      PaperProps={{ sx: { width: "94vw", maxWidth: "94vw", maxHeight: "90vh" } }}
    >
      <DialogTitle>{title} · {projectLabel}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error">{err}</Alert>}
        {loading && <CircularProgress size={24} />}
        {data && !loading && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <KpiInline label="Cost" value={fmtUsd(total.cost)} />
              <KpiInline label="Calls" value={fmtNum(total.calls)} />
              <KpiInline label="Per call" value={fmtUsdPerCall(metricValue(total, "per_call"))} />
              <KpiInline label="Prompt tokens" value={fmtNum(total.prompt)} />
              <KpiInline label="Answer tokens" value={fmtNum(total.output)} />
              <KpiInline label="Thinking tokens" value={fmtNum(total.thoughts)} />
            </Stack>
            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No LLM calls for this project on that day.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Agent", "Feature", "Company", "Model"].map((h) => (
                      <TableCell key={h} rowSpan={2} sx={{ verticalAlign: "bottom" }}>{h}</TableCell>
                    ))}
                    {metricGroupCells("day", false)}
                  </TableRow>
                  <TableRow>{metricLeafCells("day")}</TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const g = groupForFeature(r.feature, project);
                    return (
                      <TableRow key={`${r.company}|${r.model}|${r.feature}`}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}><Chip size="small" label={g.label} sx={{ bgcolor: g.color, color: "#fff" }} /></TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.feature}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.company}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.model}</TableCell>
                        {METRIC_COLS.map((mc) => <MetricCell key={mc.key} col={mc} m={r} first={false} />)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {project === "tenderq" && pipeline && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Tender pipeline that day</Typography>
                {pipeline.error ? (
                  <Alert severity="warning" sx={{ my: 1 }}>Pipeline counts unavailable: {pipeline.error}</Alert>
                ) : (
                  <>
                    <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap sx={{ my: 1 }}>
                      <KpiInline label="Tenders processed" value={fmtNum(pipeline.tender_count ?? 0)} />
                      <KpiInline label="Document parts processed" value={fmtNum(pipeline.documents ?? 0)} />
                      <KpiInline label="Documents reused" value={fmtNum(pipeline.reused ?? 0)} />
                      <KpiInline label="LLM scores computed" value={fmtNum(pipeline.scores ?? 0)} />
                    </Stack>
                    {pipeline.tenders.length > 0 && (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {sortHeader("number", "#")}
                            {sortHeader("documents", "Documents", "right")}
                            {sortHeader("parts", "Parts", "right")}
                            <TableCell>Tender</TableCell>
                            <TableCell>Lot</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lots.map((t) => (
                            <TableRow key={t.tender_id}>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{t.project_number ?? "?"}</TableCell>
                              <TableCell align="right">{t.documents == null ? "–" : fmtNum(t.documents)}</TableCell>
                              <TableCell align="right">{t.parts == null ? "–" : fmtNum(t.parts)}</TableCell>
                              <TableCell>{t.project_title ?? t.tender_id}</TableCell>
                              <TableCell>{lotText(t)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Processed = a summary was written that day (UTC, re-runs included). Document parts = metadata written that
                  day, one LLM call per part (a long document is several parts). Documents reused = completed from an identical
                  earlier copy without any LLM call. LLM scores = scores stored that day from an
                  LLM call; construction tenders excluded by the classification gate get a 0 without a call and are not counted,
                  and a score that failed is retried by the next morning's scoring run. Documents / Parts per tender = the
                  publication's whole document set, shared by all its lots. The ledger carries no tender ids, so the cost cannot
                  be split per tender.
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function ByFeatureTable({
  rows,
  project,
  visibleModels,
  showTotal,
}: {
  rows: FeatureRow[];
  project: string;
  visibleModels: string[];
  showTotal: boolean;
}) {
  const groups = groupsForProject(project);
  const grouped = groups.map((g) => ({ group: g, rows: [] as FeatureRow[] }));
  for (const r of rows) {
    const idx = groups.findIndex((g) => g.match(r.feature));
    grouped[idx >= 0 ? idx : grouped.length - 1].rows.push(r);
  }
  const nonEmpty = grouped.filter((g) => g.rows.length > 0);

  const superCols: { key: string; label: string; mono: boolean; getMetrics: (r: FeatureRow) => Metrics }[] = [
    ...visibleModels.map((m) => ({
      key: m,
      label: m,
      mono: true,
      getMetrics: (r: FeatureRow) => (r.perModel[m] ?? ZERO_METRICS),
    })),
  ];
  if (showTotal) {
    superCols.push({
      key: "__total__",
      label: "Total",
      mono: false,
      getMetrics: (r: FeatureRow) => r.total,
    });
  }

  const totalCols = 1 + superCols.length * METRIC_COLS.length;

  const groupSubtotals: Record<string, Record<string, Metrics>> = {};
  const grandTotals: Record<string, Metrics> = {};
  for (const sc of superCols) grandTotals[sc.key] = { ...ZERO_METRICS };
  for (const g of nonEmpty) {
    groupSubtotals[g.group.id] = {};
    for (const sc of superCols) {
      const sub = g.rows.reduce<Metrics>(
        (acc, r) => addMetrics(acc, sc.getMetrics(r)),
        { ...ZERO_METRICS },
      );
      groupSubtotals[g.group.id][sc.key] = sub;
      grandTotals[sc.key] = addMetrics(grandTotals[sc.key], sub);
    }
  }

  const metricCells = (m: Metrics, keyPrefix: string, weight?: number) =>
    METRIC_COLS.map((mc, j) => (
      <MetricCell key={`${keyPrefix}.${mc.key}`} col={mc} m={m} first={j === 0} weight={weight} />
    ));

  return (
    <Table size="small" sx={{ "& th, & td": { whiteSpace: "nowrap" } }}>
      <TableHead>
        <TableRow>
          <TableCell rowSpan={3} sx={{ verticalAlign: "bottom", fontWeight: 600 }}>
            Feature
          </TableCell>
          {superCols.map((sc) => (
            <TableCell
              key={sc.key}
              align="center"
              colSpan={METRIC_COLS.length}
              sx={{
                borderLeft: "1px solid",
                borderColor: "divider",
                fontWeight: 600,
                fontFamily: sc.mono ? "monospace" : undefined,
                fontSize: sc.mono ? 12 : undefined,
              }}
            >
              {sc.label}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>{superCols.flatMap((sc) => metricGroupCells(sc.key, true))}</TableRow>
        <TableRow>{superCols.flatMap((sc) => metricLeafCells(sc.key))}</TableRow>
      </TableHead>
      <TableBody>
        {nonEmpty.length === 0 && (
          <TableRow>
            <TableCell colSpan={totalCols} align="center">
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No usage recorded in this window.
              </Typography>
            </TableCell>
          </TableRow>
        )}
        {nonEmpty.flatMap((g) => [
          <TableRow key={`${g.group.id}-h`} sx={{ bgcolor: (t) => t.palette.action.selected }}>
            <TableCell colSpan={totalCols} sx={{ fontWeight: 700 }}>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  bgcolor: g.group.color,
                  borderRadius: 0.5,
                  mr: 1,
                  verticalAlign: "middle",
                }}
              />
              {g.group.label}
            </TableCell>
          </TableRow>,
          ...g.rows.map((r) => (
            <TableRow key={`${g.group.id}-${r.feature}`} hover>
              <TableCell sx={{ fontFamily: "monospace", pl: 4 }}>{r.feature}</TableCell>
              {superCols.flatMap((sc) =>
                metricCells(sc.getMetrics(r), `${g.group.id}-${r.feature}-${sc.key}`),
              )}
            </TableRow>
          )),
          <TableRow key={`${g.group.id}-sub`} sx={{ bgcolor: (t) => t.palette.action.hover }}>
            <TableCell sx={{ fontWeight: 600, fontStyle: "italic", pl: 4 }}>Subtotal</TableCell>
            {superCols.flatMap((sc) =>
              metricCells(groupSubtotals[g.group.id][sc.key], `${g.group.id}-sub-${sc.key}`, 600),
            )}
          </TableRow>,
        ])}
        {nonEmpty.length > 0 && (
          <TableRow sx={{ bgcolor: (t) => t.palette.action.focus }}>
            <TableCell sx={{ fontWeight: 700 }}>Grand total</TableCell>
            {superCols.flatMap((sc) => metricCells(grandTotals[sc.key], `grand-${sc.key}`, 700))}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function CostSection() {
  const auth = useAuth();
  const [days, setDays] = useState<number>(30);
  const [project, setProject] = useState<string>("tenderq");
  const [model, setModel] = useState<string>("all");
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  // Clicking a day in the chart opens that day's breakdown (same endpoint, ?day=).
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [dayData, setDayData] = useState<DayUsageResponse | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayErr, setDayErr] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedDay) return;
    const apiKey = auth.user?.profile?.["custom:api_key"] as string | undefined;
    const idToken = auth.user?.id_token;
    if (!apiKey || !idToken) {
      setDayErr("Authentication credentials not available.");
      return;
    }
    const baseUrl = (config as any).api.tender.url;
    const params = new URLSearchParams({ day: selectedDay });
    if (model !== "all") params.set("model", model);
    setDayLoading(true);
    setDayErr(null);
    setDayData(null);
    fetch(`${baseUrl}/admin/llm-usage?${params.toString()}`, {
      headers: { "Content-Type": "application/json", "X-Api-Key": apiKey, Authorization: idToken },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `Request failed (${res.status})`);
        }
        return res.json() as Promise<DayUsageResponse>;
      })
      .then(setDayData)
      .catch((e) => setDayErr(e.message))
      .finally(() => setDayLoading(false));
  }, [auth, selectedDay, model]);

  useEffect(() => {
    const apiKey = auth.user?.profile?.["custom:api_key"] as string | undefined;
    const idToken = auth.user?.id_token;
    if (!apiKey || !idToken) {
      setErr("Authentication credentials not available.");
      return;
    }
    // The /admin/llm-usage endpoint exists on the tender API but reads the
    // shared usage table that qabot_api_caller writes to for ALL projects;
    // we partition client-side via featureToProject().
    const baseUrl = (config as any).api.tender.url;
    const params = new URLSearchParams({ days: String(days) });
    if (model !== "all") params.set("model", model);
    const url = `${baseUrl}/admin/llm-usage?${params.toString()}`;
    setLoading(true);
    setErr(null);
    fetch(url, {
      headers: { "Content-Type": "application/json", "X-Api-Key": apiKey, Authorization: idToken },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `Request failed (${res.status})`);
        }
        return res.json() as Promise<UsageResponse>;
      })
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [auth, days, model]);

  // Group ids are scoped per project; carrying a tenderq selection into qabot
  // (or vice versa) would silently filter to nothing.
  useEffect(() => { setSelectedAgent(null); }, [project]);

  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
      <Stack spacing={2} sx={{ flex: "0 0 auto" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Stack>
            <Typography variant="h5">LLM cost overview</Typography>
            <Typography variant="caption" color="text.secondary">
              External API LLM costs only. AWS infrastructure costs coming soon.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="admin-project-label">Project</InputLabel>
              <Select
                labelId="admin-project-label"
                label="Project"
                value={project}
                onChange={(e) => setProject(e.target.value)}
              >
                {PROJECTS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="admin-model-label">Model</InputLabel>
              <Select
                labelId="admin-model-label"
                label="Model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {MODELS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              size="small"
              value={days}
              exclusive
              onChange={(_, v) => v && setDays(v)}
            >
              <ToggleButton value={7}>7 d</ToggleButton>
              <ToggleButton value={30}>30 d</ToggleButton>
              <ToggleButton value={90}>90 d</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        {err && <Alert severity="error">{err}</Alert>}
        {loading && <CircularProgress size={24} />}

        {data && !loading && (() => {
          // Recompute the KPI windows from filtered daily.byFeature so they
          // reflect only the selected project (the API's pre-rolled windows
          // include every project's spend).
          const sortedDaily = [...data.daily].sort((a, b) => a.date.localeCompare(b.date));
          const projCostFor = (p: DailyPoint): number => {
            let sum = 0;
            for (const [feat, micros] of Object.entries(p.byFeature ?? {})) {
              if (featureToProject(feat) === project) sum += micros;
            }
            return sum;
          };
          const today   = sortedDaily.length ? projCostFor(sortedDaily[sortedDaily.length - 1]) : 0;
          const last7d  = sortedDaily.slice(-7).reduce((acc, p) => acc + projCostFor(p), 0);
          const last30d = sortedDaily.slice(-30).reduce((acc, p) => acc + projCostFor(p), 0);
          return (
            <Paper variant="outlined">
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  rowGap: 1,
                }}
              >
                <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Daily spend</Typography>
                  <KpiInline label="Today" value={fmtUsd(today)} />
                  <KpiInline label="Last 7 d" value={fmtUsd(last7d)} />
                  <KpiInline label="Last 30 d" value={fmtUsd(last30d)} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  last {data.days} days · USD · stacked by agent · click a day for its details
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                <DailyCostChart
                  daily={data.daily}
                  project={project}
                  selectedAgent={selectedAgent}
                  onDayClick={setSelectedDay}
                />
              </Box>
            </Paper>
          );
        })()}
      </Stack>

      <DayDetailsDialog
        day={selectedDay}
        project={project}
        data={dayData}
        loading={dayLoading}
        err={dayErr}
        onClose={() => { setSelectedDay(null); setDayData(null); setDayErr(null); }}
      />

      {data && !loading && (() => {
        const visibleModels = orderModels(data.models ?? []);
        const showTotal = visibleModels.length > 1;
        // First, partition rows by project. Then optionally narrow to the
        // selected agent within that project.
        const projectRows = data.byFeature.filter((r) => featureToProject(r.feature) === project);
        const filteredRows = selectedAgent
          ? projectRows.filter((r) => groupForFeature(r.feature, project).id === selectedAgent)
          : projectRows;
        const selectedGroup = selectedAgent
          ? groupsForProject(project).find((g) => g.id === selectedAgent)
          : null;
        return (
          <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", pr: 0.5 }}>
            <Paper variant="outlined">
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>By feature × model</Typography>
                  {/* Agent filter as chips (the chart's click now opens a day). */}
                  {groupsForProject(project).map((g) => {
                    const on = selectedGroup?.id === g.id;
                    return (
                      <Chip
                        key={g.id}
                        size="small"
                        label={g.label}
                        clickable
                        onClick={() => setSelectedAgent(on ? null : g.id)}
                        variant={on ? "filled" : "outlined"}
                        sx={on ? { bgcolor: g.color, color: "#fff" } : { borderColor: g.color, color: g.color }}
                      />
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  grouped by agent · sorted by cost · last {data.days} days
                </Typography>
              </Box>
              <Divider />
              <ByFeatureTable
                rows={filteredRows}
                project={project}
                visibleModels={visibleModels}
                showTotal={showTotal}
              />
            </Paper>
          </Box>
        );
      })()}
    </Stack>
  );
}

// =========================================================================
//   AWS Costs — reads cost.daily_t via GET /admin/aws-costs on the chatbot API.
// =========================================================================

// Breakdown rows carry the hierarchy prefix ending at group_by. Everything
// to the left of group_by is present; anything to the right is absent.
interface AwsBreakdownRow {
  organization: string;
  customer?: string;
  category?: string;
  service?: string;
  operation?: string;
  cost: number;
  pct: number;
}
interface AwsFiltersAvailable {
  organizations: string[];
  customers: string[];
  categories: string[];
  services: string[];
}
interface AwsCostsResponse {
  range: "7d" | "30d" | "90d";
  aggregate: "day" | "week" | "month";
  group_by: "customer" | "organization" | "category" | "service" | "operation";
  start: string;
  end: string;
  kpis: { last_7d: number; last_30d: number; last_90d: number };
  series: { bucket: string; group: string; cost: number }[];
  breakdown: AwsBreakdownRow[];
  hierarchy: string[];   // e.g. ["organization","customer","category","service"]
  groups: string[];
  buckets: string[];
  filters_available: AwsFiltersAvailable;
  total: number;
  currency: string;
}

type AwsRange = "7d" | "30d" | "90d";
type AwsAggregate = "day" | "week" | "month";
type AwsGroupBy = "customer" | "organization" | "category" | "service" | "operation";
type AwsChartStyle = "stacked" | "grouped" | "line";

// Selecting a single group (chart bar / single chip) isolates that series;
// selecting a cluster isolates every leaf under a node at any nesting level.
// `value` is a path-joined key so two clusters that happen to share a label
// (unlikely but possible) don't collide; `label` is what we show to the user.
type AwsSelection =
  | { kind: "group";   value: string; groups: string[] }
  | { kind: "cluster"; value: string; label: string; groups: string[] }
  | null;

// Hierarchical order — Object.keys iterates in insertion order, so the
// Group by dropdown lists options top-down.
const AWS_GROUP_BY_LABELS: Record<AwsGroupBy, string> = {
  organization: "Organization",
  customer:     "Customer",
  category:     "Category",
  service:      "Service",
  operation:    "Operation",
};

// Ancestor chain walked when building the nested cluster tree. For service
// grouping we cluster under the category; for operation grouping we nest
// category → service so the user can collapse an entire category or drill
// down to one service's operations.
const AWS_CLUSTER_CHAIN: Partial<Record<AwsGroupBy, AwsGroupBy[]>> = {
  service:   ["category"],
  operation: ["category", "service"],
};

// Distinct-enough palette for up to 15 groups; overflow rotates back through.
const AWS_PALETTE = [
  "#1976d2", "#9c27b0", "#2e7d32", "#ed6c02", "#d32f2f",
  "#0288d1", "#7b1fa2", "#388e3c", "#f57c00", "#c62828",
  "#00796b", "#512da8", "#558b2f", "#ef6c00", "#ad1457",
];

// Rows/groups under this USD amount are treated as rounding noise when the
// "Hide <$0.01" toggle is on. Top-level totals (KPIs, API `data.total`) are
// computed server-side and stay unchanged regardless of the toggle.
const AWS_NEGLIGIBLE_THRESHOLD = 0.01;

// Mix a hex color toward white (factor > 0) or black (factor < 0). Used to
// derive sibling shades within a cluster so ten EC2 operations look related
// but remain distinguishable.
function shadeHex(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const target = factor > 0 ? 255 : 0;
  const a = Math.min(1, Math.abs(factor));
  const mix = (c: number) => Math.round(c * (1 - a) + target * a);
  const toHex = (v: number) => v.toString(16).padStart(2, "0");
  return "#" + toHex(mix(r)) + toHex(mix(g)) + toHex(mix(b));
}

// One node in the cluster tree. Either `leaves` is populated (this node's
// direct children are the group_by-level values) OR `subClusters` is
// populated (another layer of grouping sits between this node and the
// leaves). `groups` is the flattened list of leaf names under this node —
// used when the user clicks the cluster to isolate it.
interface AwsClusterNode {
  label: string;
  color: string;
  path: string[];
  groups: string[];
  leaves: { name: string; color: string }[] | null;
  subClusters: AwsClusterNode[] | null;
}

// Derives the nested cluster tree + per-group colors from the breakdown
// rows, which carry the full hierarchy prefix. Returns null at levels with
// no meaningful parent (organization/customer/category) — caller falls back
// to flat palette coloring.
function buildAwsClusters(
  groups: string[],
  breakdown: AwsBreakdownRow[],
  groupBy: AwsGroupBy,
): { clusters: AwsClusterNode[]; colorByGroup: Record<string, string> } | null {
  const chain = AWS_CLUSTER_CHAIN[groupBy];
  if (!chain || chain.length === 0) return null;

  // For each leaf, read its ancestor values straight off the breakdown rows
  // (which are pre-enriched with the full hierarchy prefix).
  const ancestorsOf: Record<string, string[]> = {};
  for (const r of breakdown) {
    const leaf = (r as any)[groupBy];
    if (!leaf || ancestorsOf[leaf]) continue;
    const path: string[] = [];
    let ok = true;
    for (const a of chain) {
      const v = (r as any)[a];
      if (!v) { ok = false; break; }
      path.push(v);
    }
    if (ok) ancestorsOf[leaf] = path;
  }
  if (Object.keys(ancestorsOf).length === 0) return null;

  interface Builder {
    label: string;
    order: string[];
    children: Record<string, Builder>;
    leaves: string[];
  }
  const mkBuilder = (label: string): Builder => ({ label, order: [], children: {}, leaves: [] });

  // Walk groups in server order so the tree follows the cost-desc sort.
  const rootOrder: string[] = [];
  const rootChildren: Record<string, Builder> = {};
  for (const g of groups) {
    const ancestors = ancestorsOf[g];
    if (!ancestors) continue;
    let parentOrder = rootOrder;
    let parentMap = rootChildren;
    let node: Builder | null = null;
    for (const a of ancestors) {
      if (!parentMap[a]) {
        parentMap[a] = mkBuilder(a);
        parentOrder.push(a);
      }
      node = parentMap[a];
      parentOrder = node.order;
      parentMap = node.children;
    }
    if (node) node.leaves.push(g);
  }

  const colorByGroup: Record<string, string> = {};
  const finalize = (b: Builder, color: string, path: string[]): AwsClusterNode => {
    if (b.order.length > 0) {
      const n = b.order.length;
      const subClusters = b.order.map((lbl, i) => {
        const factor = n > 1 ? -0.25 + (0.6 * i) / (n - 1) : 0;
        return finalize(b.children[lbl], shadeHex(color, factor), path.concat(lbl));
      });
      const gs: string[] = [];
      subClusters.forEach((s) => s.groups.forEach((g) => gs.push(g)));
      return { label: b.label, color, path, groups: gs, leaves: null, subClusters };
    }
    const n = b.leaves.length;
    const leaves = b.leaves.map((name, i) => {
      const factor = n > 1 ? -0.25 + (0.6 * i) / (n - 1) : 0;
      const leafColor = shadeHex(color, factor);
      colorByGroup[name] = leafColor;
      return { name, color: leafColor };
    });
    return { label: b.label, color, path, groups: b.leaves.slice(), leaves, subClusters: null };
  };

  const clusters = rootOrder.map((lbl, i) =>
    finalize(rootChildren[lbl], AWS_PALETTE[i % AWS_PALETTE.length], [lbl])
  );
  return { clusters, colorByGroup };
}

// Plain-USD formatter (distinct from fmtUsd which operates on micros).
const fmtUsdPlain = (usd: number): string => {
  const n = Number.isFinite(usd) ? usd : 0;
  const whole = Math.trunc(n);
  const cents = Math.abs(n - whole).toFixed(2).slice(2);
  return `$${fmtNum(whole)}.${cents}`;
};

// Custom legend — colored chips, click to toggle a highlight. Replaces
// ECharts' native legend so we can style and gesture-handle properly.
// When `clusters` is provided (service/operation grouping), only the top-
// level cluster chips are rendered inline; hovering one opens a popover
// with the expanded hierarchy (services → operations) so the legend stays
// compact even with hundreds of leaves.
function AwsCostsLegend({
  groups,
  clusters,
  colorByGroup,
  selection,
  onSelect,
  hiddenGroups,
}: {
  groups: string[];
  clusters: AwsClusterNode[] | null;
  colorByGroup: Record<string, string>;
  selection: AwsSelection;
  onSelect: (sel: AwsSelection) => void;
  hiddenGroups: Set<string>;
}) {
  const visibleGroups = groups.filter((g) => !hiddenGroups.has(g));
  if (visibleGroups.length === 0) return null;

  const isInSelection = (g: string) =>
    selection === null || selection.groups.indexOf(g) !== -1;

  const renderLeafChip = (name: string, color: string) => {
    if (hiddenGroups.has(name)) return null;
    const isGroupSelected = selection?.kind === "group" && selection.value === name;
    const isDimmed = !isInSelection(name);
    return (
      <Chip
        key={name}
        size="small"
        label={name}
        onClick={() =>
          onSelect(isGroupSelected ? null : { kind: "group", value: name, groups: [name] })
        }
        sx={{
          cursor: "pointer",
          borderLeft: `10px solid ${color}`,
          borderRadius: "6px",
          pl: 0.5,
          fontWeight: isGroupSelected ? 700 : 500,
          bgcolor: isGroupSelected ? color : "background.default",
          color: isGroupSelected ? "#fff" : "text.primary",
          opacity: isDimmed ? 0.35 : 1,
          transition: "opacity 120ms, background-color 120ms",
          "&:hover": { bgcolor: isGroupSelected ? color : "action.hover" },
        }}
      />
    );
  };

  const renderClusterChip = (node: AwsClusterNode, opts?: { bold?: boolean; showCount?: boolean }) => {
    const visibleInCluster = node.groups.filter((g) => !hiddenGroups.has(g));
    if (visibleInCluster.length === 0) return null;
    const key = node.path.join("▶");
    const isClusterSelected = selection?.kind === "cluster" && selection.value === key;
    const anyChildActive = visibleInCluster.some((g) => isInSelection(g));
    const isDimmed = selection !== null && !isClusterSelected && !anyChildActive;
    const label = opts?.showCount
      ? `${node.label} (${visibleInCluster.length})`
      : node.label;
    return (
      <Chip
        key={key}
        size="small"
        label={label}
        onClick={() =>
          onSelect(
            isClusterSelected
              ? null
              : { kind: "cluster", value: key, label: node.label, groups: node.groups.slice() }
          )
        }
        sx={{
          cursor: "pointer",
          borderLeft: `10px solid ${node.color}`,
          borderRadius: "6px",
          pl: 0.5,
          fontWeight: opts?.bold ? 700 : 500,
          bgcolor: isClusterSelected ? node.color : "background.default",
          color: isClusterSelected ? "#fff" : "text.primary",
          opacity: isDimmed ? 0.45 : 1,
          transition: "opacity 120ms, background-color 120ms",
          "&:hover": { bgcolor: isClusterSelected ? node.color : "action.hover" },
        }}
      />
    );
  };

  // Popover body: for a category with direct service leaves (service mode),
  // one row of chips. For a category with services that each contain
  // operations (operation mode), one row per service — service chip (bold,
  // cluster-selectable) followed by its operation chips.
  const renderPopoverBody = (node: AwsClusterNode) => {
    if (node.leaves) {
      const visibleLeaves = node.leaves.filter((l) => !hiddenGroups.has(l.name));
      if (visibleLeaves.length === 0) return null;
      return (
        <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.75, maxWidth: 720 }}>
          {visibleLeaves.map((l) => renderLeafChip(l.name, l.color))}
        </Stack>
      );
    }
    if (node.subClusters) {
      const visibleSubs = node.subClusters.filter((sub) =>
        sub.groups.some((g) => !hiddenGroups.has(g)),
      );
      if (visibleSubs.length === 0) return null;
      return (
        <Stack sx={{ gap: 0.75, maxWidth: 780 }}>
          {visibleSubs.map((sub) => (
            <Stack
              key={sub.path.join("▶")}
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ gap: 0.75 }}
            >
              {renderClusterChip(sub, { bold: true, showCount: true })}
              {sub.leaves
                ?.filter((l) => !hiddenGroups.has(l.name))
                .map((l) => renderLeafChip(l.name, l.color))}
            </Stack>
          ))}
        </Stack>
      );
    }
    return null;
  };

  if (!clusters) {
    return (
      <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.75, px: 2, pt: 1 }}>
        {visibleGroups.map((g) => renderLeafChip(g, colorByGroup[g] ?? AWS_PALETTE[0]))}
        {selection && (
          <Chip size="small" variant="outlined" label="Clear"
            onClick={() => onSelect(null)} sx={{ borderRadius: "6px" }}/>
        )}
      </Stack>
    );
  }

  const visibleClusters = clusters.filter((c) =>
    c.groups.some((g) => !hiddenGroups.has(g)),
  );
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 0.75, px: 2, pt: 1 }}>
      {visibleClusters.map((c) => (
        <Tooltip
          key={c.path.join("▶")}
          arrow
          placement="bottom-start"
          enterDelay={150}
          leaveDelay={250}
          title={<Box>{renderPopoverBody(c)}</Box>}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: "none",
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                boxShadow: 3,
                p: 1.5,
              },
            },
            arrow: {
              sx: {
                color: "background.paper",
                "&::before": { border: "1px solid", borderColor: "divider" },
              },
            },
          }}
        >
          {renderClusterChip(c, { bold: true, showCount: true }) ?? <span />}
        </Tooltip>
      ))}
      {selection && (
        <Chip size="small" variant="outlined" label="Clear"
          onClick={() => onSelect(null)} sx={{ borderRadius: "6px" }}/>
      )}
    </Stack>
  );
}

function AwsCostsChart({
  data,
  selection,
  colorByGroup,
  onGroupClick,
  chartStyle,
  hiddenGroups,
}: {
  data: AwsCostsResponse;
  selection: AwsSelection;
  colorByGroup: Record<string, string>;
  onGroupClick: (group: string) => void;
  chartStyle: AwsChartStyle;
  hiddenGroups: Set<string>;
}) {
  // Pivot: { group -> { bucket -> cost } }
  const m = new Map<string, Map<string, number>>();
  for (const r of data.series) {
    let row = m.get(r.group);
    if (!row) { row = new Map(); m.set(r.group, row); }
    row.set(r.bucket, r.cost);
  }

  // When a selection is active (single group or cluster), render ONLY those
  // series — ECharts auto-rescales the y-axis to the smaller range. Groups
  // flagged as hidden (negligible) are dropped so they don't show up in the
  // tooltip either.
  const visibleGroups = (selection
    ? data.groups.filter((g) => selection.groups.indexOf(g) !== -1)
    : data.groups
  ).filter((g) => !hiddenGroups.has(g));

  const isLine = chartStyle === "line";
  const series = visibleGroups.map((g) => {
    const s: any = {
      id: g,
      name: g,
      type: isLine ? "line" : "bar",
      data: data.buckets.map((b) => Number((m.get(g)?.get(b) ?? 0).toFixed(4))),
      itemStyle: { color: colorByGroup[g] ?? AWS_PALETTE[0] },
      emphasis: { focus: "series" },
      cursor: "pointer",
    };
    if (isLine) {
      s.smooth = true;
      s.showSymbol = false;
      s.lineStyle = { width: 2, color: colorByGroup[g] ?? AWS_PALETTE[0] };
    } else if (chartStyle === "stacked") {
      // Stacked → one stack name across all series; Grouped → no stack.
      s.stack = "total";
    }
    return s;
  });

  // Per-bucket total for the tooltip header — reflects only the visible
  // series so the "Total" line matches what the bars actually show.
  const perBucketTotal: Record<string, number> = {};
  for (const r of data.series) {
    if (hiddenGroups.has(r.group)) continue;
    if (selection && selection.groups.indexOf(r.group) === -1) continue;
    perBucketTotal[r.bucket] = (perBucketTotal[r.bucket] ?? 0) + r.cost;
  }

  return (
    <EChart
      style={{ width: "100%", height: 240 }}
      grid={{ left: 64, right: 16, top: 12, bottom: 36 }}
      legend={{ show: false }}
      tooltip={{
        trigger: "axis",
        axisPointer: { type: isLine ? "line" : "shadow" },
        formatter: (params: any) => {
          const arr = Array.isArray(params) ? params : [params];
          if (arr.length === 0) return "";
          const bucket = arr[0].axisValue;
          const lines = [`<b>${bucket}</b>`];
          lines.push(`Total: <b>${fmtUsdPlain(perBucketTotal[bucket] ?? 0)}</b>`);
          const sorted = arr
            .filter((p: any) => Number(p.value) > 0)
            .sort((a: any, b: any) => Number(b.value) - Number(a.value));
          for (const p of sorted) {
            lines.push(`${p.marker} ${p.seriesName}: ${fmtUsdPlain(Number(p.value))}`);
          }
          return lines.join("<br/>");
        },
      }}
      xAxis={{
        type: "category",
        data: data.buckets,
        axisLabel: { fontSize: 11, hideOverlap: true },
      }}
      yAxis={{
        type: "value",
        axisLabel: { formatter: (v: number) => fmtUsdPlain(v), fontSize: 11 },
      }}
      series={series}
      onClick={(p: any) => {
        if (!p || p.componentType !== "series") return;
        onGroupClick(p.seriesName as string);
      }}
    />
  );
}

// Hierarchical breakdown — rows grouped by the full prefix ending at group_by,
// with bolded subtotal rows at every parent level (organization, customer,
// category, ...). Columns always appear in fixed hierarchy order so the eye
// can read the table left-to-right.
interface AwsTreeNode {
  label: string;
  level: number;         // 0 = organization, 1 = customer, ...
  cost: number;
  path: string[];        // hierarchy values from root down to this node
  children: AwsTreeNode[];
}

function buildAwsTree(rows: AwsBreakdownRow[], hierarchy: AwsGroupBy[]): AwsTreeNode[] {
  const root: AwsTreeNode = { label: "", level: -1, cost: 0, path: [], children: [] };
  for (const r of rows) {
    let node = root;
    for (let i = 0; i < hierarchy.length; i++) {
      const val = (r as any)[hierarchy[i]];
      if (val === undefined || val === null) break;
      const label = String(val);
      let child = node.children.find((c) => c.label === label);
      if (!child) {
        child = { label, level: i, cost: 0, path: [...node.path, label], children: [] };
        node.children.push(child);
      }
      child.cost += r.cost;
      node = child;
    }
  }
  const sortNode = (n: AwsTreeNode) => {
    n.children.sort((a, b) => b.cost - a.cost);
    n.children.forEach(sortNode);
  };
  sortNode(root);
  return root.children;
}

interface AwsFlatRow {
  node: AwsTreeNode;
  isLeaf: boolean;
}

function flattenAwsTree(nodes: AwsTreeNode[], includeSubtotals: boolean = true): AwsFlatRow[] {
  const out: AwsFlatRow[] = [];
  for (const n of nodes) {
    const isLeaf = n.children.length === 0;
    if (isLeaf || includeSubtotals) out.push({ node: n, isLeaf });
    if (!isLeaf) out.push(...flattenAwsTree(n.children, includeSubtotals));
  }
  return out;
}

function AwsCostsBreakdownTable({
  rows,
  hierarchy,
  total,
  showSubtotals,
}: {
  rows: AwsBreakdownRow[];
  hierarchy: AwsGroupBy[];
  total: number;
  showSubtotals: boolean;
}) {
  const flat = flattenAwsTree(buildAwsTree(rows, hierarchy), showSubtotals);
  const colSpan = hierarchy.length + 2;
  const subtotalBg = "action.hover";

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {hierarchy.map((h) => (
            <TableCell key={h} sx={{ fontWeight: 600 }}>{AWS_GROUP_BY_LABELS[h]}</TableCell>
          ))}
          <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>% of total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {flat.length === 0 && (
          <TableRow>
            <TableCell colSpan={colSpan} align="center">
              <Typography variant="caption" color="text.secondary">No data for this range.</Typography>
            </TableCell>
          </TableRow>
        )}
        {flat.map(({ node, isLeaf }, idx) => {
          const pct = total > 0 ? (node.cost / total) * 100 : 0;
          return (
            <TableRow
              key={`${node.path.join("|")}|${idx}`}
              hover
              sx={!isLeaf ? { bgcolor: subtotalBg } : {}}
            >
              {hierarchy.map((h, i) => {
                const val = i <= node.level ? node.path[i] : "";
                return (
                  <TableCell
                    key={h}
                    sx={!isLeaf ? { fontWeight: 700 } : {}}
                  >
                    {val}
                  </TableCell>
                );
              })}
              <TableCell align="right" sx={!isLeaf ? { fontWeight: 700 } : {}}>
                {fmtUsdPlain(node.cost)}
              </TableCell>
              <TableCell align="right" sx={!isLeaf ? { fontWeight: 700 } : {}}>
                {pct.toFixed(1)}%
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function AwsCostsSection() {
  const auth = useAuth();
  const [range, setRange] = useState<AwsRange>("30d");
  const [aggregate, setAggregate] = useState<AwsAggregate>("day");
  const [groupBy, setGroupBy] = useState<AwsGroupBy>("organization");
  const [chartStyle, setChartStyle] = useState<AwsChartStyle>("stacked");
  const [showSubtotals, setShowSubtotals] = useState<boolean>(true);
  const [hideNegligible, setHideNegligible] = useState<boolean>(true);
  const [organization, setOrganization] = useState<string>("");
  const [customer, setCustomer]         = useState<string>("");
  const [category, setCategory]         = useState<string>("");
  const [service, setService]           = useState<string>("");
  const [data, setData] = useState<AwsCostsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selection, setSelection] = useState<AwsSelection>(null);

  // Reset the local highlight when the grouping dimension or filters shift —
  // the old group label may not exist in the new dataset.
  useEffect(() => { setSelection(null); }, [groupBy, range, aggregate, organization, customer, category, service]);

  // Picking a new category invalidates the prior service filter (a service
  // only makes sense within its category).
  useEffect(() => { setService(""); }, [category]);

  useEffect(() => {
    const apiKey = auth.user?.profile?.["custom:api_key"] as string | undefined;
    const idToken = auth.user?.id_token;
    if (!apiKey || !idToken) {
      setErr("Authentication credentials not available.");
      return;
    }
    const baseUrl = (config as any).api.chatbot.url;
    const params = new URLSearchParams({ range, aggregate, group_by: groupBy });
    if (organization) params.set("organization", organization);
    if (customer)     params.set("customer", customer);
    if (category)     params.set("category", category);
    if (service)      params.set("service", service);
    const url = `${baseUrl}/admin/aws-costs?${params.toString()}`;
    setLoading(true);
    setErr(null);
    fetch(url, {
      headers: { "Content-Type": "application/json", "X-Api-Key": apiKey, Authorization: idToken },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `Request failed (${res.status})`);
        }
        return res.json() as Promise<AwsCostsResponse>;
      })
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [auth, range, aggregate, groupBy, organization, customer, category, service]);

  const available = data?.filters_available ?? { organizations: [], customers: [], categories: [], services: [] };
  const anyFilterSet = !!(organization || customer || category || service);

  // Cluster services by category (operations by service) and derive colors
  // from the parent palette — siblings within a cluster share a family so
  // the chart reads as "shades of Compute" rather than 20 random hues.
  const clustersInfo = useMemo<{ clusters: AwsClusterNode[] | null; colorByGroup: Record<string, string> }>(() => {
    if (!data) return { clusters: null, colorByGroup: {} };
    const clustered = buildAwsClusters(data.groups, data.breakdown, data.group_by);
    if (clustered) return clustered;
    const flat: Record<string, string> = {};
    data.groups.forEach((g: string, i: number) => { flat[g] = AWS_PALETTE[i % AWS_PALETTE.length]; });
    return { clusters: null, colorByGroup: flat };
  }, [data]);
  const clusters = clustersInfo.clusters;
  const colorByGroup = clustersInfo.colorByGroup;

  // Per-group totals across the range — used to decide which groups fall
  // under the negligible threshold and should be dropped from the
  // legend/chart. We sum from `series` (not `breakdown`) so this matches
  // exactly what the chart would plot.
  const groupTotals = useMemo<Record<string, number>>(() => {
    if (!data) return {};
    const totals: Record<string, number> = {};
    for (const r of data.series) {
      totals[r.group] = (totals[r.group] ?? 0) + r.cost;
    }
    return totals;
  }, [data]);

  const hiddenGroups = useMemo<Set<string>>(() => {
    if (!data || !hideNegligible) return new Set();
    const s = new Set<string>();
    for (const g of data.groups) {
      if ((groupTotals[g] ?? 0) < AWS_NEGLIGIBLE_THRESHOLD) s.add(g);
    }
    return s;
  }, [data, hideNegligible, groupTotals]);

  // Chart/legend click narrows the breakdown to rows whose group_by value
  // is in the selection (one group, or every child of a clicked cluster).
  // Then, when the toggle is on, drop rows under the threshold — subtotals
  // in the table are derived from the filtered rows so parent totals stay
  // consistent with what's visible beneath them. Top-level `data.total`
  // and the KPI trio come from the API and are never touched by this.
  const { filteredBreakdown, hiddenLeafCount } = useMemo(() => {
    if (!data) return { filteredBreakdown: [] as AwsBreakdownRow[], hiddenLeafCount: 0 };
    const scoped: AwsBreakdownRow[] = selection
      ? data.breakdown.filter((r: AwsBreakdownRow) =>
          selection.groups.indexOf((r as any)[data.group_by]) !== -1,
        )
      : data.breakdown;
    if (!hideNegligible) return { filteredBreakdown: scoped, hiddenLeafCount: 0 };
    const kept = scoped.filter((r: AwsBreakdownRow) => r.cost >= AWS_NEGLIGIBLE_THRESHOLD);
    return { filteredBreakdown: kept, hiddenLeafCount: scoped.length - kept.length };
  }, [data, selection, hideNegligible]);
  const breakdownHierarchy = (data?.hierarchy ?? []) as AwsGroupBy[];

  // Chart bars always toggle single-group selection; cluster selection is
  // only reachable from the legend.
  const onChartClick = (g: string) => {
    setSelection(
      selection?.kind === "group" && selection.value === g
        ? null
        : { kind: "group", value: g, groups: [g] },
    );
  };

  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
      <Stack spacing={2} sx={{ flex: "0 0 auto" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Stack>
            <Typography variant="h5">AWS cost overview</Typography>
            <Typography variant="caption" color="text.secondary">
              AWS infrastructure costs, attributed per customer across all configured Organizations.
              Matches the Billing & Cost Management console to the cent.
            </Typography>
          </Stack>
          <ToggleButtonGroup
            size="small"
            value={range}
            exclusive
            onChange={(_, v: AwsRange | null) => v && setRange(v)}
          >
            <ToggleButton value="7d">7 d</ToggleButton>
            <ToggleButton value="30d">30 d</ToggleButton>
            <ToggleButton value="90d">90 d</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="aws-group-by-label">Group by</InputLabel>
            <Select
              labelId="aws-group-by-label"
              label="Group by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as AwsGroupBy)}
            >
              {(Object.keys(AWS_GROUP_BY_LABELS) as AwsGroupBy[]).map((k) => (
                <MenuItem key={k} value={k}>{AWS_GROUP_BY_LABELS[k]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="aws-aggregate-label">Aggregate</InputLabel>
            <Select
              labelId="aws-aggregate-label"
              label="Aggregate"
              value={aggregate}
              onChange={(e) => setAggregate(e.target.value as AwsAggregate)}
            >
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="week">Weekly</MenuItem>
              <MenuItem value="month">Monthly</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            value={chartStyle}
            exclusive
            onChange={(_, v: AwsChartStyle | null) => v && setChartStyle(v)}
          >
            <ToggleButton value="grouped" aria-label="Grouped bars">
              <Tooltip title="Grouped bars"><BarChartIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="line" aria-label="Line">
              <Tooltip title="Line"><ShowChartIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="stacked" aria-label="Stacked bars">
              <Tooltip title="Stacked bars"><StackedBarChartIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="aws-org-filter-label">Organization</InputLabel>
            <Select
              labelId="aws-org-filter-label"
              label="Organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {available.organizations.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="aws-customer-filter-label">Customer</InputLabel>
            <Select
              labelId="aws-customer-filter-label"
              label="Customer"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {available.customers.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="aws-category-filter-label">Category</InputLabel>
            <Select
              labelId="aws-category-filter-label"
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {available.categories.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="aws-service-filter-label">Service</InputLabel>
            <Select
              labelId="aws-service-filter-label"
              label="Service"
              value={service}
              onChange={(e) => setService(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {available.services.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {anyFilterSet && (
            <Chip
              size="small"
              variant="outlined"
              label="Clear filters"
              onClick={() => { setOrganization(""); setCustomer(""); setCategory(""); setService(""); }}
              sx={{ ml: 0.5 }}
            />
          )}
        </Stack>

        {err && <Alert severity="error">{err}</Alert>}
        {loading && <CircularProgress size={24} />}

        {data && !loading && (
          <Paper variant="outlined">
            <Box
              sx={{
                px: 2, py: 1, display: "flex",
                justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2, rowGap: 1,
              }}
            >
              <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Spend</Typography>
                <KpiInline label="Last 7 d"  value={fmtUsdPlain(data.kpis.last_7d)} />
                <KpiInline label="Last 30 d" value={fmtUsdPlain(data.kpis.last_30d)} />
                <KpiInline label="Last 90 d" value={fmtUsdPlain(data.kpis.last_90d)} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {data.start} → {data.end} · {data.aggregate} · {chartStyle} by {AWS_GROUP_BY_LABELS[data.group_by].toLowerCase()} · click a chip or bar to highlight
              </Typography>
            </Box>
            <Divider />
            <AwsCostsLegend
              groups={data.groups}
              clusters={clusters}
              colorByGroup={colorByGroup}
              selection={selection}
              onSelect={setSelection}
              hiddenGroups={hiddenGroups}
            />
            <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
              <AwsCostsChart
                data={data}
                selection={selection}
                colorByGroup={colorByGroup}
                onGroupClick={onChartClick}
                chartStyle={chartStyle}
                hiddenGroups={hiddenGroups}
              />
            </Box>
          </Paper>
        )}
      </Stack>

      {data && !loading && (
        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", pr: 0.5 }}>
          <Paper variant="outlined">
            <Box
              sx={{
                px: 2, py: 1, display: "flex",
                justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Breakdown by {AWS_GROUP_BY_LABELS[data.group_by].toLowerCase()}
                </Typography>
                {selection && (
                  <Chip
                    size="small"
                    label={
                      selection.kind === "cluster"
                        ? `Cluster: ${selection.label} (${selection.groups.length})`
                        : `Highlighted: ${selection.value}`
                    }
                    onDelete={() => setSelection(null)}
                  />
                )}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showSubtotals}
                      onChange={(_e: unknown, checked: boolean) => setShowSubtotals(checked)}
                    />
                  }
                  label={<Typography variant="caption">Subtotals</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={hideNegligible}
                      onChange={(_e: unknown, checked: boolean) => setHideNegligible(checked)}
                    />
                  }
                  label={<Typography variant="caption">{"Hide < $0.01"}</Typography>}
                  sx={{ m: 0 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Total: {fmtUsdPlain(data.total)} · {filteredBreakdown.length} rows
                {hiddenLeafCount > 0 && ` · +${hiddenLeafCount} hidden`}
              </Typography>
            </Box>
            <Divider />
            <AwsCostsBreakdownTable rows={filteredBreakdown} hierarchy={breakdownHierarchy} total={data.total} showSubtotals={showSubtotals} />
          </Paper>
        </Box>
      )}
    </Stack>
  );
}

function OtherSection() {
  return (
    <Stack spacing={1}>
      <Typography variant="h5">Other</Typography>
      <Typography variant="body2" color="text.secondary">
        Placeholder — additional admin tools will live here.
      </Typography>
    </Stack>
  );
}

// Tab keys in the URL (?tab=budgets&budget=12) so budget mails can link to one budget.
const ADMIN_TABS = ["api", "aws", "budgets", "other"];

function Admin() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = Math.max(0, ADMIN_TABS.indexOf(params.get("tab") ?? "api"));
  const setTab = (index: number) => {
    const next = new URLSearchParams(params);
    next.set("tab", ADMIN_TABS[index]);
    if (ADMIN_TABS[index] !== "budgets") next.delete("budget");
    setParams(next, { replace: true });
  };

  const groups = (auth.user?.profile?.["cognito:groups"] as string[] | undefined) || [];
  const isAuthorized = auth.isAuthenticated && (groups.includes("admin") || groups.includes("quantum"));

  if (!isAuthorized) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You don't have access to the admin panel.</Alert>
      </Box>
    );
  }

  return (
    <Stack direction="column" spacing={2} sx={{ height: "100%", p: 3, minHeight: 0 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab label="API Costs" />
        <Tab label="AWS Costs" />
        <Tab label="Budgets" />
        <Tab label="Other" />
      </Tabs>
      <Divider />
      <Box sx={{ pt: 1, flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {tab === 0 && <CostSection />}
        {tab === 1 && <AwsCostsSection />}
        {tab === 2 && <BudgetsSection />}
        {tab === 3 && <OtherSection />}
      </Box>
    </Stack>
  );
}

export default Admin;
